/**
 * Jovii — WHEN A TRAILER MAY START BY ITSELF.
 *
 * The rules behind `trailer.svelte.ts`, in a plain module with no runes in it,
 * so that "would this start a trailer over a film?" can be argued with in a
 * test rather than in a browser. `trailer.svelte.ts` re-exports every name here;
 * import from there unless you are a test.
 */

/* ────────────────────────────────────────────────────────────────────────────
 * Dwell
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The banner is furniture and moves slowly, so five seconds is long enough to
 * mean "this is what I am looking at" and short enough to happen before the
 * slide turns at eight.
 */
/*
 * THE BANNER STARTS ITS TRAILER ALMOST AT ONCE. This was 5000 — five seconds of
 * a still picture before anything moved, on the one surface whose whole job is
 * to show you something. The owner's rule: the still holds for about two
 * seconds, the trailer runs, and the slide advances when it is done.
 */
export const HERO_DWELL_MS = 2000;

/**
 * WHEN A TILE STARTS LOADING ITS TRAILER — INVISIBLY (the owner's 2026-08-19
 * choreography: "load the trailer in the background").
 *
 * At this much BANKED attention (the dwell is a stopwatch — `dwell.ts` — so a
 * thumb pausing through a scroll still gets here) the tile takes the one
 * trailer claim and the shared stage starts the embed muted and hidden.
 * Nothing on the glass changes: the ring and the strip carry on, and the
 * BRAND_MS branding wait burns down where nobody can see it.
 *
 * This is also the sweep-rejection floor the old TILE_DWELL_MS carried: a
 * pointer crossing a card is on it ~150 ms, a thumb skidding down a page banks
 * a few hundred — the floor test in trailer-rules.test.ts now guards THIS
 * number, because this is the moment bytes are spent.
 */
export const TILE_PRELOAD_MS = 3000;

/**
 * WHEN THE TILE MAY EXPAND — and the expansion IS the reveal.
 *
 * The old shape (expand at 1.4 s, then seconds of a still in a 16:9 card
 * waiting for the reveal) made the growth a promise the video kept late. The
 * owner's rule: the card grows ONLY when the moving picture is ready to be in
 * it — `tileExpandDue` below gates on this bank AND the reveal verdict, so
 * motion is on the glass within the expansion's own 240 ms. If the video is
 * not ready at 7 s the compact tile simply holds and expands whenever
 * readiness arrives; if it will never play (no id, embedding disabled,
 * autoplay refused), the tile NEVER expands — the expanded-still card no
 * longer exists.
 *
 * REVISED BY THE OWNER after feeling it on his phone (2026-08-19): expand at
 * SIX seconds, then let the expanded card show its banner art for about two
 * seconds (`TILE_CARD_STILL_MS`) before the video takes over. So the gate
 * loosened from "branding-cleared" to "provably rolling": at 6 s the burn is
 * usually mid-flight (preload 3 s + PLAYING ~0.8 s + BRAND_MS 3.5 s ≈ 7.3 s),
 * and the banner beat is what absorbs the rest of it — the test now pins
 * TILE_EXPAND_MS + TILE_CARD_STILL_MS ≥ TILE_PRELOAD_MS + BRAND_MS. "No
 * trailer, no expansion" stands unchanged: rolling is still required.
 */
export const TILE_EXPAND_MS = 6000;

/**
 * THE BANNER BEAT: how long the expanded card shows its artwork before the
 * video is uncovered. The owner asked to SEE the expanded banner of the film
 * for a moment rather than have motion wipe in with the growth. The uncover
 * additionally waits for the reveal verdict's 'show' (branding cleared), so
 * this is a minimum, not a metronome.
 */
export const TILE_CARD_STILL_MS = 2000;

/**
 * HOW LONG THE BANNER GIVES EACH SLIDE'S TRAILER — the slideshow's own clock,
 * not a data cap any more.
 *
 * The owner's rule (2026-08-17 QA): the trailer plays thirty seconds and then
 * the SLIDESHOW ADVANCES — the cap is a page-turn, never a freeze. `Hero.svelte`
 * enforces exactly that: reaching this bound calls the same advance the
 * trailer's own ENDED does, so a trailer longer than the slide's turn hands the
 * banner to the next title rather than parking it on a stopped frame. A trailer
 * that ends sooner advances sooner. And when the cycle comes back around, the
 * slide plays its trailer AGAIN, from the beginning — the banner is a channel
 * now, and thirty seconds a slide is what keeps it feeling like one
 * (docs/TRAILERS.md § D1: ~4.1–4.7 MB per 30 s is also what one slide's turn
 * costs, which is why the number did not move when the meaning did).
 *
 * MUST STAY FINITE. The advance-at-cap is a real setTimeout in Hero.svelte; an
 * infinite hero cap would mean the slideshow never turns. The tile bound below
 * is the one that was retired.
 */
export const HERO_TRAILER_MAX_MS = 30_000;

/**
 * RETIRED: THE 30-SECOND BOUND ON A TILE'S TRAILER.
 *
 * Owner's rule (2026-08-17 QA): a focused tile plays its ENTIRE trailer, and
 * may play it again on the next visit — the only thing that stops it is the
 * user moving away (focus loss already tears the embed down, and still does).
 * What still bounds the data: the dwell (a sweep starts nothing), one trailer
 * at a time app-wide, ENDED brings the still back rather than looping, and
 * Save-Data / Reduced-Motion / a film in progress / a hidden tab refuse the
 * start entirely.
 *
 * `Infinity`, HONESTLY: any surface enforcing this cap must SKIP its timer when
 * the bound is not finite — `setTimeout(fn, Infinity)` fires IMMEDIATELY in
 * browsers (the delay is coerced through a 32-bit conversion to 0), which would
 * kill every tile trailer at birth. Guard with `Number.isFinite(...)` before
 * arming anything with this constant. Poster.svelte owns the tile timer.
 */
export const TILE_TRAILER_MAX_MS = Number.POSITIVE_INFINITY;

/**
 * RETIRED: THE PER-VISIT CAP ON AUTOMATIC TRAILERS.
 *
 * This was 2 — two automatic starts per SPA session, EVER — set by the data
 * research (one embed is ~4.1–4.7 MB, docs/TRAILERS.md § D1, D4). Then the
 * hero was redesigned to CYCLE: still → trailer → advance on end → next slide,
 * which spends the whole budget on the first two slides, after which every
 * hero slide and every tile is a still for the rest of the session. The owner
 * QA'd exactly that (2026-08-16): "after using the app for a bit of time, the
 * trailers never start again" — a defect report, not thrift. A cap the visitor
 * can feel expire reads as the app breaking, so the rule no longer consults
 * it.
 *
 * What still bounds the data, and is enough: a trailer needs real attention to
 * start (the dwell stopwatch), each one is cut off at 30 s
 * (`HERO_TRAILER_MAX_MS` / `TILE_TRAILER_MAX_MS`), one plays at a time, a
 * slide never replays its own (`bannerSpent`), and Save-Data, Reduced-Motion,
 * a film in progress, or a hidden tab each stop everything.
 *
 * The constant survives as documentation of the retired rule (bannerSpent
 * points here); nothing reads it. A deliberate press never consulted any of
 * this.
 */
export const AUTO_TRAILER_BUDGET = Infinity;

/* ────────────────────────────────────────────────────────────────────────────
 * The reveal — a still first, and no sign that it is YouTube
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * HOW LONG YOUTUBE'S OWN BRANDING IS ON SCREEN AFTER THE VIDEO STARTS.
 *
 * Measured, three videos, screenshots at fixed offsets from the `PLAYING` event
 * with the title strip and the wordmark corner sampled for brightness
 * (docs/TRAILERS.md § B1): the title bar, the channel avatar and the "YouTube"
 * wordmark are on screen from the FIRST FRAME and are gone between 3000 and
 * 3400 ms. It is the player's own three-second chrome auto-hide, and because
 * our frame carries `pointer-events: none` the timer never restarts.
 *
 * So revealing the video when it starts playing is the WORST possible moment —
 * it is the earliest instant the branding is up. This is 3400 plus a hundred.
 */
export const BRAND_MS = 3500;

/**
 * The minimum time the still holds, whatever the video does.
 *
 * The owner asked for "five or six seconds of the banner, larger, in the shape
 * the video will be, and then the trailer". Five is also comfortably past
 * `BRAND_MS` on the measured median time-to-play (781 ms + 3400 = 4.2 s), so
 * the picture that arrives has no YouTube on it with ~800 ms to spare.
 */
/* How long OUR still is held over the frame while YouTube's branding clears.
   Shortened with the dwell: the picture is ready long before this expired. */
export const STILL_HOLD_MS = 2200;

/**
 * How long to wait for a device to say ANYTHING before deciding it will not
 * play. `PLAYING` arrives at a median 781 ms and `onError` at ~529 ms, so six
 * seconds is not a race — it is the branch that covers every reason an iPhone
 * refuses (Low Power Mode, Low Data Mode, an embed a phone will not autoplay),
 * none of which is detectable and all of which want the same answer: keep the
 * still, offer a tap, change nothing else.
 */
export const SILENCE_MS = 6000;

/** How long the cross-fade from still to video takes. */
export const REVEAL_FADE_MS = 400;

export interface RevealInput {
  /** When the stage opened and the iframe was mounted. */
  openedAt: number;
  /** When `onStateChange` reported PLAYING, or null if it has not. */
  playingAt: number | null;
  /** `onError` arrived — an id nobody may embed. */
  errored: boolean;
  /** The minimum still hold for this surface. `STILL_HOLD_MS`, or 0 for a press. */
  holdMs?: number;
}

/**
 * What the stage should be showing.
 *
 *  • `hold`       — our still, and the video may yet arrive.
 *  • `show`       — cross-fade to the video; the branding is gone.
 *  • `still-only` — it is never going to play here. Keep the still, offer a tap,
 *                   and do not remove anything from the screen.
 */
export type RevealVerdict = 'hold' | 'show' | 'still-only';

export function revealVerdict(input: RevealInput, now: number): RevealVerdict {
  const hold = input.holdMs ?? STILL_HOLD_MS;
  if (input.playingAt !== null) {
    const due = Math.max(input.playingAt + BRAND_MS, input.openedAt + hold);
    return now >= due ? 'show' : 'hold';
  }
  if (input.errored) return 'still-only';
  return now - input.openedAt >= SILENCE_MS ? 'still-only' : 'hold';
}

/**
 * WHETHER A TILE MAY EXPAND, and it is an AND of two facts:
 *
 *   • `bankedMs` — attention actually banked on the tile's stopwatch has
 *     reached `TILE_EXPAND_MS`. Time alone is never enough: a card stared at
 *     for a minute whose video never played must not grow.
 *   • the video is PROVABLY GOING TO PLAY: either the verdict is already
 *     `'show'` (rolling and branding-free), or the player is `rolling` —
 *     PLAYING has arrived and the branding burn is merely mid-flight, which
 *     the expanded card's banner beat (`TILE_CARD_STILL_MS`) covers. The
 *     uncover itself still waits for `'show'`; this gate only opens the card.
 *
 * `'still-only'` can never satisfy this, whatever the bank or the rolling
 * flag: no trailer, no expansion, ever. `'hold'` without rolling means the
 * compact tile waits (the dwell keeps banking meanwhile).
 */
export function tileExpandDue(bankedMs: number, verdict: RevealVerdict, rolling: boolean): boolean {
  if (bankedMs < TILE_EXPAND_MS || verdict === 'still-only') return false;
  return verdict === 'show' || rolling;
}

/**
 * Milliseconds until the verdict could change, so a caller can arm exactly one
 * timer instead of polling. `Infinity` means nothing more will happen.
 */
export function revealIn(input: RevealInput, now: number): number {
  if (input.errored) return Infinity;
  if (input.playingAt !== null) {
    const hold = input.holdMs ?? STILL_HOLD_MS;
    return Math.max(0, Math.max(input.playingAt + BRAND_MS, input.openedAt + hold) - now);
  }
  return Math.max(0, input.openedAt + SILENCE_MS - now);
}

/* ────────────────────────────────────────────────────────────────────────────
 * The black bars — measuring a trailer's true picture from its own thumbnails
 * ──────────────────────────────────────────────────────────────────────────── */

/*
 * A trailer whose picture is wider than 16:9 arrives with the letterbox BURNED
 * INTO the video (docs/TRAILERS.md § E4: the IFrame API cannot report
 * dimensions, and YouTube rasterises everything to 16:9), so the bars paint a
 * hard black edge against whatever surrounds the embed. The one honest way to
 * know the true picture is to look at the video's own frames — and YouTube
 * publishes three of them for every id: `i.ytimg.com/vi/<id>/mq{1,2,3}.jpg`,
 * 320×180 (true 16:9, the video raster), served with `ACAO: *` so a
 * crossorigin canvas may read them. Probed 2026-08-17 on live catalogue
 * trailers:
 *
 *   • a 2.39:1 trailer shows top=bottom=23px bars in ALL THREE frames;
 *   • a 3:2 trailer shows left=right=25px in all three;
 *   • a dark SCENE shows a deep run in ONE frame and not the others
 *     (one probe: left=26,25,118 across the three frames).
 *
 * That last line is the design: a burned-in bar is black in EVERY frame, so
 * the combined answer is the MINIMUM across frames, and a single moody shot
 * can never invent a crop. `mqdefault` is deliberately not used — it is the
 * uploader's chosen thumbnail, usually full-bleed art with no bars in it.
 * YouTube's oEmbed was probed too and rejected: it answers 200×113 (16:9)
 * even for a 2.39:1 trailer, because it reports the RASTER, never the bars.
 *
 * The maths lives here, off the DOM, so "would this decapitate an actor?" is
 * arguable in a test. `trailer-crop.ts` owns the fetching and the canvas.
 */

/**
 * How bright a pixel may be and still be "bar". JPEG ringing puts a faint glow
 * on the content side of a bar edge; 24 (of 255) clears the measured bars and
 * stops at the first row with any real picture in it.
 */
export const BAR_LUMA = 24;

/**
 * THE DECAPITATION CAP. Removing a 2.39:1 trailer's bars entirely needs a
 * 1.34× zoom, which throws away a quarter of the picture's width — faces at
 * the edges of frame go with it. 15% linear is the most this app will crop:
 * it erases the common 1.85–2.0:1 bars completely and HALVES the 2.39:1 ones,
 * and an honest residual bar beats a beheaded actor every time.
 */
export const CROP_MAX_SCALE = 1.15;

/** Bars thinner than this (per edge, as a fraction) are not worth a zoom. */
export const CROP_MIN_EDGE = 0.02;

/**
 * Bars deeper than this are not bars — a 2.39:1 letterbox is 12.8% per edge,
 * a 4:3 pillarbox 12.5%; past 30% it is a title card on black, so no verdict.
 */
export const CROP_MAX_EDGE = 0.3;

/** How many frames must agree. One frame alone can always be a dark scene. */
export const CROP_MIN_SCANS = 2;

/** One thumbnail's edge runs, in pixels, plus the raster they were read from. */
export interface BarScan {
  width: number;
  height: number;
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * How deep the dark runs from each end of a row/column-maxima array. Pure so
 * the JPEG-edge behaviour is testable; `trailer-crop.ts` feeds it real pixels.
 */
export function barDepths(
  maxes: ArrayLike<number>,
  threshold: number = BAR_LUMA
): { lead: number; trail: number } {
  const n = maxes.length;
  let lead = 0;
  while (lead < n && (maxes[lead] as number) < threshold) lead += 1;
  let trail = 0;
  while (trail < n - lead && (maxes[n - 1 - trail] as number) < threshold) trail += 1;
  return { lead, trail };
}

export interface CropVerdict {
  /** Linear zoom for the embed (and the echo). 1 means "leave it alone". */
  scale: number;
  /** Which bars were found, for tests and the debug read. */
  axis: 'letterbox' | 'pillarbox' | null;
}

const NO_CROP: CropVerdict = { scale: 1, axis: null };

/**
 * One axis's two edges, as a total bar fraction — or null when they do not
 * read as bars. Both edges must be real (a bar has two sides; one dark edge is
 * a night sky), neither may be implausibly deep, and they must be near-equal:
 * YouTube centres its letterbox, so asymmetry means a dark composition, and a
 * centred zoom against asymmetric bars would crop picture on the shallow side.
 */
function axisBars(a: number, b: number): number | null {
  if (a < CROP_MIN_EDGE || b < CROP_MIN_EDGE) return null;
  if (a > CROP_MAX_EDGE || b > CROP_MAX_EDGE) return null;
  if (Math.abs(a - b) > Math.max(0.015, 0.3 * Math.max(a, b))) return null;
  return a + b;
}

/**
 * The verdict over every frame that loaded. Uncertainty ALWAYS answers
 * `scale: 1` — an uncropped bar is a cosmetic defect, an overcropped face is
 * a broken product — and the zoom is capped at `CROP_MAX_SCALE` even when the
 * bars honestly ask for more.
 */
export function cropVerdict(scans: BarScan[]): CropVerdict {
  if (scans.length < CROP_MIN_SCANS) return NO_CROP;
  let top = Infinity;
  let bottom = Infinity;
  let left = Infinity;
  let right = Infinity;
  for (const s of scans) {
    if (!(s.width > 0) || !(s.height > 0)) return NO_CROP;
    top = Math.min(top, s.top / s.height);
    bottom = Math.min(bottom, s.bottom / s.height);
    left = Math.min(left, s.left / s.width);
    right = Math.min(right, s.right / s.width);
  }
  const y = axisBars(top, bottom);
  const x = axisBars(left, right);
  // Bars on both axes is a frame with a border around it, not a letterbox.
  if (y !== null && x !== null) return NO_CROP;
  const bars = y ?? x;
  if (bars === null) return NO_CROP;
  return {
    scale: Math.min(CROP_MAX_SCALE, 1 / (1 - bars)),
    axis: y !== null ? 'letterbox' : 'pillarbox'
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * The rules
 * ──────────────────────────────────────────────────────────────────────────── */

export interface AutoplayConditions {
  /** There is at least one trailer id to play. */
  hasTrailer: boolean;
  /** `prefers-reduced-motion: reduce`. */
  reducedMotion: boolean;
  /** `navigator.connection.saveData`. */
  saveData: boolean;
  /** A film is being watched in this browser, in any tab. */
  filmInProgress: boolean;
  /** `document.visibilityState === 'hidden'`. */
  backgrounded: boolean;
  /**
   * This banner has already spent its one automatic trailer this visit.
   *
   * HISTORICAL — the per-visit banner budget is retired (see
   * `AUTO_TRAILER_BUDGET`) and no caller passes this any more. The field and
   * its refusal survive so old callers and tests stay honest about what the
   * rule USED to be.
   */
  bannerSpent?: boolean;
  /**
   * Which surface is asking. Absent means a tile — the default is the common
   * case, and Poster.svelte does not have to know this rule exists.
   */
  surface?: 'hero' | 'tile';
  /**
   * THE BANNER OWNS THE TOP OF THE PAGE, while it is actually there.
   *
   * On a touch screen the ambient focus is a GUESS — "the leftmost card of the
   * row nearest the middle" — and when the page is at the very top that guess
   * lands on the first rail's first tile even though the banner fills the top
   * half of the glass. Measured (2026-08-17, 390x844): the tile claimed the
   * one trailer slot at 1.9 s and the banner cycled silent stills for ever.
   * So while the banner is SUBSTANTIALLY on screen in centre-focus mode and
   * nothing has been deliberately chosen (no tap, no arrow key — an ambient
   * guess is not a choice), a TILE's automatic trailer is refused and the
   * banner runs its channel. A deliberate choice always wins over primacy:
   * `Hero.svelte` drops this flag the moment the focus source is anything but
   * the centre guess, and once the banner is mostly scrolled away the flag
   * goes with it and the tiles inherit the screen. Never set under a pointer,
   * where hover already arbitrates honestly.
   */
  heroPrimary?: boolean;
}

/** Why a trailer is not starting. `null` means it may. */
export type AutoplayRefusal =
  | 'no-trailer'
  | 'reduced-motion'
  | 'save-data'
  | 'film'
  | 'background'
  | 'banner-spent'
  | 'hero-primacy';

export function autoplayRefusal(c: AutoplayConditions): AutoplayRefusal | null {
  if (!c.hasTrailer) return 'no-trailer';
  if (c.reducedMotion) return 'reduced-motion';
  if (c.saveData) return 'save-data';
  if (c.filmInProgress) return 'film';
  if (c.backgrounded) return 'background';
  if (c.bannerSpent === true) return 'banner-spent';
  if (c.surface !== 'hero' && c.heroPrimary === true) return 'hero-primacy';
  return null;
}

export function mayAutoplay(c: AutoplayConditions): boolean {
  return autoplayRefusal(c) === null;
}

/* ────────────────────────────────────────────────────────────────────────────
 * "A film is in progress" — across tabs, because that is where it bites
 * ──────────────────────────────────────────────────────────────────────────── */

const FILM_KEY = 'jt.watching.v1';

/**
 * How long a heartbeat is believed. The watch screen writes one every
 * `FILM_BEAT_MS`; three missed beats and we assume the tab is gone, because a
 * tab that is killed from the switcher says nothing on the way out
 * (docs/OPEN-DEFECTS.md B1 — there is no reliable goodbye on iOS).
 */
export const FILM_BEAT_MS = 4000;
const FILM_TRUST_MS = FILM_BEAT_MS * 3;

function now(): number {
  return Date.now();
}

/**
 * Called by the watch screen while it is mounted. Cheap on purpose: one
 * `localStorage` write every four seconds, never on the media path.
 */
export function beatFilmInProgress(): void {
  try {
    localStorage.setItem(FILM_KEY, String(now()));
  } catch {
    /* private mode: the guard degrades to "this tab only", which is still true */
  }
}

/** Called when the watch screen goes away. */
export function endFilmInProgress(): void {
  try {
    localStorage.removeItem(FILM_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** True when some tab of this browser is watching something right now. */
export function filmInProgress(): boolean {
  try {
    const raw = localStorage.getItem(FILM_KEY);
    if (raw === null) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    // A clock that has gone backwards (a laptop waking up) must not pin the
    // guard on for ever.
    const age = now() - at;
    return age >= 0 && age < FILM_TRUST_MS;
  } catch {
    return false;
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Resuming
 * ──────────────────────────────────────────────────────────────────────────── */

/** Where a trailer had got to when it was given up. */
export interface TrailerMemo {
  ytId: string;
  sec: number;
  at: number;
}

/** How long a memo is worth anything. This is about the second between pressing
 *  a tile and the next screen drawing, not a viewing history. */
export const MEMO_TTL_MS = 45_000;

/**
 * How far into `ytId` to start, given what was playing a moment ago.
 *
 * Counts the time spent getting here as well: a trailer that was 11 s in when
 * the tile was pressed is 13 s in by the time the title page has drawn, and
 * resuming at 11 would replay two seconds somebody has already watched.
 */
export function resumeFrom(memo: TrailerMemo | null, ytId: string, at: number): number {
  if (memo === null || memo.ytId !== ytId) return 0;
  const since = at - memo.at;
  if (since < 0 || since > MEMO_TTL_MS) return 0;
  return memo.sec + Math.floor(since / 1000);
}
